import React from 'react'
import { useTranslation } from 'react-i18next'

import { Contract, FeatureFlag } from '../../../common/validations'
import { Layout } from './Layout/Layout'
import { useModuleConfig } from '../../hooks/useModuleConfig/useModuleConfig'

import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'
import routePath from 'router/routePath'
import { Permissions } from 'components'

interface TaskLinksProps {
  featureFlags: FeatureFlag
  contract: Contract
}

export const TaskLinks: React.FC<TaskLinksProps> = ({
  featureFlags,
  contract,
}) => {
  const modules = useModuleConfig()
  const { t } = useTranslation(['common'])
  const { defectNoticePath, paymentHistoryPath, saunaReservationsPath } =
    useContractPaths()

  const fullTaskPaths = {
    terminateContract: routePath.terminateContract.replace(
      ':contractId',
      contract.uuid
    ),
    residentChanges: routePath.residentChanges.replace(
      ':contractId',
      contract.uuid
    ),
    defectNotice: defectNoticePath,
    payments: paymentHistoryPath,
    saunaReservation: saunaReservationsPath,
  }

  // defect notice enabled unless contractTerminationOnly or explicitly false
  const defectNoticeEnabled =
    !modules.contractTerminationOnly && modules.defectNotice.enabled !== false
  const externalDefectUrl = defectNoticeEnabled
    ? modules.defectNotice.customDefectNoticeLinkUrl
    : undefined
  const contractModification = modules.contractModification ?? false

  return (
    <Layout.NavContainer>
      {defectNoticeEnabled &&
        (externalDefectUrl ? (
          <Layout.StyledNavLink
            to={externalDefectUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Layout.LinkLabel
              label={t('common:taskLinks.defectNotice')}
              icon="reparation"
            />
          </Layout.StyledNavLink>
        ) : (
          <Layout.StyledNavLink to={fullTaskPaths.defectNotice}>
            <Layout.LinkLabel
              label={t('common:taskLinks.defectNotice')}
              icon="reparation"
            />
          </Layout.StyledNavLink>
        ))}
      {!modules.contractTerminationOnly && (
        <Permissions feature="payments">
          <Layout.StyledNavLink
            to={fullTaskPaths.payments}
            disabled={!featureFlags.paymentInfo}
          >
            <Layout.LinkLabel
              label={t('common:taskLinks.payments')}
              icon="euro-sign-circle"
              hint={
                !featureFlags.paymentInfo ? t('common:comingSoon') : undefined
              }
            />
          </Layout.StyledNavLink>
        </Permissions>
      )}
      {!modules.contractTerminationOnly && modules.sauna.enabled && (
        <Layout.StyledNavLink to={fullTaskPaths.saunaReservation}>
          <Layout.LinkLabel
            label={t('common:taskLinks.saunaReservation')}
            icon="sauna"
          />
        </Layout.StyledNavLink>
      )}
      {contractModification && (
        <Permissions feature="contractChanges">
          <Layout.StyledNavLink to={fullTaskPaths.residentChanges}>
            <Layout.LinkLabel
              label={t('common:taskLinks.residentChanges')}
              icon="group"
            />
          </Layout.StyledNavLink>
        </Permissions>
      )}
      <Permissions feature="contractChanges">
        <Layout.StyledNavLink to={fullTaskPaths.terminateContract}>
          <Layout.LinkLabel
            label={t('common:taskLinks.moveNotice')}
            icon="moving"
          />
        </Layout.StyledNavLink>
      </Permissions>
    </Layout.NavContainer>
  )
}
